import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { DataTag } from '../MediaFactory';
import './AgentEditor.css';

/**
 * AgentEditor component - Editor for configuring AI agents on factories
 * 
 * @param {Object} props
 * @param {Array} props.agents - Current array of agent configurations
 * @param {function} props.onAgentsChange - Callback when agents are modified
 * @param {boolean} props.showMqttConfig - Whether to show MQTT configuration UI
 * @param {Object} props.mqttConfig - Current MQTT configuration
 * @param {function} props.onMqttConfigChange - Callback when MQTT config is modified
 * @param {string} props.mediaUrl - URL of factory image for visual positioning
 * @param {Array} props.existingNodes - Array of existing nodes (data tags/assets) to display on preview
 *   Each node should have: { name, position: { x, y }, color }
 * @param {string} props.nodeStyle - Style of existing nodes: "dataTag" (MediaFactory) or "beacon" (standard factory)
 * @param {boolean} props.isSimulated - Whether agents source is simulated (topicBase becomes optional). Deprecated: use groupDataSource instead.
 * @param {string} props.groupDataSource - Effective group-level data source ('live' | 'simulated'). Each agent can override via agent.dataSource.
 * @param {boolean} props.compact - When true, renders only the list + simplified dialog without image positioning
 * @param {function} props.requireDataSource - Callback that returns false if data source not yet chosen (opens prompt)
 * @param {function} props.onReady - Called with { openAddDialog } so parent can trigger add programmatically
 */
const AgentEditor = ({
  agents = [],
  onAgentsChange,
  showMqttConfig = false,
  mqttConfig = null,
  onMqttConfigChange,
  mediaUrl = '',
  existingNodes = [],
  nodeStyle = 'dataTag',
  isSimulated: isSimulatedLegacy = false,
  groupDataSource = '',
  compact = false,
  requireDataSource,
  onReady,
}) => {
  // Resolve per-agent effective data source
  const getAgentDataSource = useCallback((agent) => {
    return agent.dataSource || groupDataSource || (isSimulatedLegacy ? 'simulated' : 'live');
  }, [groupDataSource, isSimulatedLegacy]);

  const isAgentSimulated = useCallback((agent) => {
    return getAgentDataSource(agent) === 'simulated';
  }, [getAgentDataSource]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [thoughtsText, setThoughtsText] = useState('');
  const mediaContainerRef = useRef(null);

  const generateId = () => `agent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const openAddDialogRef = useRef(null);
  openAddDialogRef.current = () => {
    setEditingAgent(createDefaultAgent());
    setThoughtsText('');
    setEditDialogOpen(true);
  };

  useEffect(() => {
    onReady?.({ openAddDialog: () => openAddDialogRef.current?.() });
  }, [onReady]);

  // Create default agent object
  const createDefaultAgent = (position = { x: 50, y: 50 }) => ({
    id: generateId(),
    name: 'New Agent',
    topicBase: '',
    position,
    style: {
      color: '#B07AA1',
    },
  });

  const handleMqttFieldChange = (field, value) => {
    onMqttConfigChange?.({
      ...mqttConfig,
      [field]: value,
    });
  };

  // Calculate position as percentage from click event on the dialog image
  const handleImageClick = useCallback((e) => {
    if (!mediaContainerRef.current) return;
    
    const rect = mediaContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newPosition = {
      x: Math.max(5, Math.min(95, x)),
      y: Math.max(5, Math.min(95, y)),
    };

    setEditingAgent(prev => ({
      ...prev,
      position: newPosition,
    }));
  }, []);

  const handleAddAgent = useCallback(() => {
    if (requireDataSource && !requireDataSource("agent")) return;
    setEditingAgent(createDefaultAgent());
    setThoughtsText('');
    setEditDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireDataSource]);

  // Open edit dialog for an existing agent
  const handleEditAgent = useCallback((agent) => {
    setEditingAgent({ ...agent });
    setThoughtsText((agent.simulatedThoughts || []).join('\n'));
    setEditDialogOpen(true);
  }, []);

  // Delete an agent
  const handleDeleteAgent = useCallback((agentId) => {
    const updatedAgents = agents.filter(agent => agent.id !== agentId);
    onAgentsChange(updatedAgents);
  }, [agents, onAgentsChange]);

  const handleSaveAgent = useCallback(() => {
    if (!editingAgent) return;

    const agentToSave = { ...editingAgent };
    const nodeSimulated = isAgentSimulated(agentToSave);
    if (nodeSimulated) {
      const parsed = thoughtsText
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      if (parsed.length > 0) {
        agentToSave.simulatedThoughts = parsed;
      } else {
        delete agentToSave.simulatedThoughts;
      }
    }

    const existingIndex = agents.findIndex(a => a.id === agentToSave.id);

    let updatedAgents;
    if (existingIndex >= 0) {
      updatedAgents = agents.map(a => a.id === agentToSave.id ? agentToSave : a);
    } else {
      updatedAgents = [...agents, agentToSave];
    }

    onAgentsChange(updatedAgents);
    setEditDialogOpen(false);
    setEditingAgent(null);
  }, [editingAgent, agents, onAgentsChange, isAgentSimulated, thoughtsText]);

  // Update editing agent field
  const handleEditingAgentChange = useCallback((field, value) => {
    setEditingAgent(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Update editing agent style field
  const handleEditingAgentStyleChange = useCallback((field, value) => {
    setEditingAgent(prev => ({
      ...prev,
      style: {
        ...prev.style,
        [field]: value,
      },
    }));
  }, []);

  // Update editing agent position field
  const handleEditingAgentPositionChange = useCallback((field, value) => {
    setEditingAgent(prev => ({
      ...prev,
      position: {
        ...prev.position,
        [field]: Math.max(0, Math.min(100, parseFloat(value) || 0)),
      },
    }));
  }, []);

  const renderAgentsList = () => (
    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
      {agents.length === 0 ? (
        <ListItem>
          <ListItemText 
            secondary="No agents configured. Click 'Add Agent' to create one."
          />
        </ListItem>
      ) : (
        agents.map((agent) => {
          const nodeSimulated = isAgentSimulated(agent);
          return (
          <ListItem
            key={agent.id}
            sx={{ cursor: 'pointer' }}
            onClick={() => handleEditAgent(agent)}
          >
            <Box
              component="label"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '4px',
                backgroundColor: agent.style?.color || '#B07AA1',
                border: '1px solid #ccc',
                cursor: 'pointer',
                mr: 1,
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <input
                type="color"
                value={agent.style?.color || '#B07AA1'}
                onChange={(e) => {
                  e.stopPropagation();
                  const updatedAgents = agents.map(a =>
                    a.id === agent.id ? { ...a, style: { ...a.style, color: e.target.value } } : a
                  );
                  onAgentsChange(updatedAgents);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
              />
            </Box>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {agent.name}
                  <Chip
                    label={nodeSimulated ? 'Sim' : 'Live'}
                    size="small"
                    color={nodeSimulated ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              }
              secondary={nodeSimulated
                ? (agent.simulatedThoughts?.length
                  ? `${agent.simulatedThoughts.length} custom thoughts`
                  : 'Auto-generated thoughts')
                : (agent.topicBase || 'No topic set')}
              secondaryTypographyProps={{
                noWrap: true,
                style: { maxWidth: 200 },
              }}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditAgent(agent);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAgent(agent.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
          );
        })
      )}
    </List>
  );

  const editingNodeSimulated = editingAgent ? isAgentSimulated(editingAgent) : false;

  const renderFormFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: compact ? 1 : 0 }}>
      <TextField
        label="Agent Name"
        value={editingAgent?.name || ''}
        onChange={(e) => handleEditingAgentChange('name', e.target.value)}
        fullWidth
        helperText="Display name shown in the agent header"
      />

      {/* Per-node data source override */}
      {groupDataSource && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Data Source</Typography>
          <ToggleButtonGroup
            value={editingAgent?.dataSource || 'inherit'}
            exclusive
            onChange={(e, val) => {
              if (val !== null) {
                handleEditingAgentChange('dataSource', val === 'inherit' ? '' : val);
              }
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="inherit">
              Inherit ({groupDataSource === 'live' ? 'Live' : 'Sim'})
            </ToggleButton>
            <ToggleButton value="live">Live MQTT</ToggleButton>
            <ToggleButton value="simulated">Simulated</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {!editingNodeSimulated && (
        <TextField
          label="Topic Base"
          value={editingAgent?.topicBase || ''}
          onChange={(e) => handleEditingAgentChange('topicBase', e.target.value)}
          fullWidth
          helperText="Base MQTT topic path (e.g., agents/shift-review). Will subscribe to /status and /thought subtopics."
          placeholder="agents/my-agent"
        />
      )}

      <TextField
        label="Color"
        type="color"
        value={editingAgent?.style?.color || '#B07AA1'}
        onChange={(e) => handleEditingAgentStyleChange('color', e.target.value)}
        fullWidth
        InputProps={{
          sx: { height: 56 }
        }}
      />

      {editingNodeSimulated && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Simulation Settings</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
            If left empty, thoughts are auto-generated based on keywords in the agent name
            (e.g., "shift", "quality", "maintenance").
          </Typography>
          <TextField
            label="Custom Thoughts (one per line)"
            value={thoughtsText}
            onChange={(e) => setThoughtsText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            size="small"
            placeholder={"Loading production data\nAnalyzing efficiency metrics\nGenerating summary report"}
            helperText="Each line becomes one thought step in the simulation cycle"
          />
        </>
      )}

      {!compact && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Position {mediaUrl ? '(or click image)' : '(percentage from top-left)'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="X Position (%)"
              type="number"
              value={editingAgent?.position?.x?.toFixed(1) || 50}
              onChange={(e) => handleEditingAgentPositionChange('x', e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              sx={{ flex: 1 }}
            />
            
            <TextField
              label="Y Position (%)"
              type="number"
              value={editingAgent?.position?.y?.toFixed(1) || 50}
              onChange={(e) => handleEditingAgentPositionChange('y', e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              sx={{ flex: 1 }}
            />
          </Box>
        </>
      )}
    </Box>
  );

  if (compact) {
    return (
      <Box className="agent-editor-container">
        <Box
          sx={{
            border: '1px solid #BDBDBD',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddAgent}
            >
              Add Agent
            </Button>
          </Box>
          <Divider />
          {renderAgentsList()}
        </Box>

        {/* Compact Edit Agent Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          TransitionProps={{ onExited: () => setEditingAgent(null) }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingAgent && agents.some(a => a.id === editingAgent.id) ? 'Edit Agent' : 'Add New Agent'}
          </DialogTitle>
          <DialogContent>
            {renderFormFields()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveAgent} 
              variant="contained"
              disabled={!editingAgent?.name || (!editingNodeSimulated && !editingAgent?.topicBase)}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box className="agent-editor-container">
      {/* MQTT Configuration Section (optional) */}
      {showMqttConfig && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            MQTT Broker Configuration
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Configure the MQTT broker that agents will connect to for receiving thoughts.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Broker URL"
              size="small"
              fullWidth
              value={mqttConfig?.broker || ''}
              onChange={(e) => handleMqttFieldChange('broker', e.target.value)}
              placeholder="wss://your-broker.com:8884/mqtt"
              helperText="WebSocket URL of your MQTT broker"
            />
            <TextField
              label="Username"
              size="small"
              fullWidth
              value={mqttConfig?.username || ''}
              onChange={(e) => handleMqttFieldChange('username', e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              fullWidth
              value={mqttConfig?.password || ''}
              onChange={(e) => handleMqttFieldChange('password', e.target.value)}
            />
          </Box>
          
          <Divider sx={{ my: 3 }} />
        </Box>
      )}

      {/* Info note about data source */}
      {!showMqttConfig && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {groupDataSource === 'simulated'
            ? 'Group default is simulated. Individual agents can override to live.'
            : groupDataSource === 'live'
              ? 'Live agents will use the MQTT broker from the factory\'s "Data Source" section. Individual agents can override to simulated.'
              : 'Agents will use the MQTT broker configured in the factory\'s "Data Source" section.'}
        </Alert>
      )}

      {/* Instructions */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Configure AI agents that will display floating thought panels on the factory visualization.
        Each agent can independently use live MQTT or simulated data.
      </Typography>

      {/* Agents List */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Agents ({agents.length})
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddAgent}
            variant="outlined"
          >
            Add Agent
          </Button>
        </Box>
        
        <Divider />
        {renderAgentsList()}
      </Paper>

      {/* Edit Agent Dialog - with visual positioning */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        TransitionProps={{ onExited: () => setEditingAgent(null) }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingAgent && agents.some(a => a.id === editingAgent.id) ? 'Edit Agent' : 'Add New Agent'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Visual Position Editor */}
            {mediaUrl && (
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Click on the image to set agent position
                </Typography>
                <div
                  ref={mediaContainerRef}
                  className="agent-editor-dialog-media-container"
                  onClick={handleImageClick}
                >
                  <img
                    className="agent-editor-dialog-media"
                    src={mediaUrl}
                    alt="Factory"
                  />
                  
                  {/* Existing nodes (data tags/assets) - read-only */}
                  {existingNodes.map((node, index) => {
                    const pos = node.position || { x: 50, y: 50 };
                    const color = node.color || '#4E79A7';
                    
                    if (nodeStyle === 'beacon') {
                      return (
                        <div
                          key={node.id || `node-${index}`}
                          className="agent-editor-beacon-wrapper"
                          style={{
                            left: `${pos.x}%`,
                            top: `${pos.y}%`,
                            '--beacon-color': color,
                          }}
                        >
                          <div className="agent-editor-beacon-dot" style={{ backgroundColor: color }} />
                          <div className="agent-editor-beacon-ring" />
                          <div className="agent-editor-beacon">
                            {node.name}
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <DataTag
                        key={node.id || `node-${index}`}
                        label={node.name}
                        value="---"
                        position={pos}
                        style={{ color: color }}
                      />
                    );
                  })}

                  {/* Current agent marker */}
                  {editingAgent && (
                    <div
                      className="agent-editor-dialog-marker"
                      style={{
                        left: `${editingAgent.position?.x || 50}%`,
                        top: `${editingAgent.position?.y || 50}%`,
                        '--marker-color': editingAgent.style?.color || '#B07AA1',
                      }}
                    >
                      <SmartToyIcon className="agent-editor-dialog-marker-icon" />
                      <span className="agent-editor-dialog-marker-label">
                        {editingAgent.name || 'New Agent'}
                      </span>
                    </div>
                  )}
                </div>
              </Grid>
            )}

            {/* Form Fields */}
            <Grid item xs={12} md={mediaUrl ? 5 : 12}>
              {renderFormFields()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveAgent} 
            variant="contained"
            disabled={!editingAgent?.name || (!editingNodeSimulated && !editingAgent?.topicBase)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentEditor;
